import { useMutation, useQueryClient } from "@tanstack/react-query";
import i18n from "@/i18n";
import { apiFetch } from "./apiClient";

/**
 * Jazyk rozhraní. Volba se ukládá k účtu (`auth_users.preferred_language`) a do
 * cookie `ETTS_LOCALE`, takže platí i pro texty renderované serverem (nápověda,
 * popisy funkcí, chybové hlášky).
 */
export const SUPPORTED_LOCALES = ["cs", "sk", "en"] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];

/** `"cs-CZ"` → `"cs"`, nepodporovaný tag → `null`. */
export function normalizeLocale(tag: string | null | undefined): AppLocale | null {
  if (!tag) return null;
  const base = tag.toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LOCALES.includes(base as AppLocale) ? (base as AppLocale) : null;
}

/**
 * Přepne jazyk. Bez reloadu stránky — `window.__ETTS__.locale` se čte jen při bootu
 * a `EttsLocaleResolver` preferuje cookie, takže server i klient se okamžitě shodnou.
 *
 * `i18n.changeLanguage` se volá až po úspěchu, ne optimisticky: jinak by mezi
 * přepnutím a doručením cookie odešel request se starým `Accept-Language`.
 */
export function useChangeLocale() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lang: AppLocale) =>
      apiFetch<void>("/admin/api/v1/locale", { method: "POST", json: { lang } }),
    onSuccess: async (_data, lang) => {
      await i18n.changeLanguage(lang); // listener v i18n/index.ts přenastaví Accept-Language
      // Refetch všeho, co nese server-side lokalizované texty (docs HTML, popisy features…).
      await queryClient.invalidateQueries();
    },
  });
}
